import { InputRule, inputRules } from 'prosemirror-inputrules'
import { Fragment, Node, Schema } from 'prosemirror-model'
import { TextSelection } from 'prosemirror-state'
import { ourUuid } from 'utils'

// Markdown-style list creation for the textBox editor: "- "/"* " -> bullet, "1. "/"1) " -> numbered.
// The full doc editor has a richer InputRules plugin; this is a self-contained subset for the
// textBox's minimal ProseMirror setup, building the prose-core list -> list_item -> paragraph shape.

// Replace the just-typed paragraph (matched by `regExp`) with the node `build` returns, carrying the
// paragraph's inline content minus the typed marker. No-op inside an existing list item.
function replaceParagraphInputRule(
  regExp: RegExp,
  build: (match: string[], inline: Fragment, paragraph: Node, schema: Schema) => Node | null,
) {
  return new InputRule(regExp, (state, match, start) => {
    const $start = state.doc.resolve(start)
    const $before = state.doc.resolve($start.before())
    const paragraph = $before.nodeAfter
    if (!paragraph || $before.node().type === state.schema.nodes.list_item) return null

    const inline = Fragment.from(paragraph.content.content.filter((node) => node.isInline))
    const node = build(match, inline, paragraph, state.schema)
    if (!node) return null

    const tr = state.tr.replaceWith($before.pos, $start.after(), node)
    tr.setSelection(TextSelection.near(tr.doc.resolve($start.pos)))
    return tr
  })
}

const listItemWith = (schema: Schema, paragraph: Node, inline: Fragment, markerLength: number) =>
  schema.nodes.list_item.create(
    { depth: paragraph.attrs.depth || 0 },
    schema.nodes.paragraph.create({}, inline.cut(markerLength)),
  )

export const listInputRules = (schema: Schema) =>
  inputRules({
    rules: [
      // "- " or "* " -> bullet list
      replaceParagraphInputRule(/^[*-]\s$/, ([match], inline, paragraph, s) =>
        s.nodes.unordered_list.create({ blockId: ourUuid() }, listItemWith(s, paragraph, inline, match.length - 1)),
      ),
      // "1. " / "1) " (1-6 digits) -> numbered list. Always starts at 1 — no marker renderer (editor
      // CSS counters, RichTextContent, monospace) honors a start offset, so the typed number is not
      // captured.
      replaceParagraphInputRule(/^[0-9]{1,6}[.)]\s$/, ([match], inline, paragraph, s) =>
        s.nodes.ordered_list.create({ blockId: ourUuid() }, listItemWith(s, paragraph, inline, match.length - 1)),
      ),
    ],
  })
